import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { editIssueRequest } from "../../../../utils/APICalls";
import {
  getCreateIssueSearchOrderRequest,
  getIssueDetailsRequest,
  getOrderIssuesRequest,
} from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { userType } from "../../../../utils/Constant";

export default function EditAcceptanceIssue({
  orders,
  issueId,
  issueDetails,
  onHide,
}: any) {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [orderOptions, setOrderOptions] = useState<string[]>([]);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { createIssueSearchResults } = useSelector(
    (state: any) => state.orders
  );
  const { currentUser } = useSelector((state: any) => state.user.user);

  const validationSchema = Yup.object({
    subType: Yup.string().required("This field can't be empty"),
    orderIds: Yup.array().min(1, "This field can't be empty"),
    description: Yup.string().required("This field can't be empty"),
  });

  const formik = useFormik({
    initialValues: {
      subType: issueDetails?.subType || "Acceptance",
      orderIds: [] as string[],
      description: issueDetails?.description || "",
      onHoldUntilResolved: true,
    },
    validationSchema,
    onSubmit: (values) => {
      const payload: any = {
        subType: values.subType,
        orderIds: values.orderIds,
        description: values.description,
        onHoldUntilResolved: values.onHoldUntilResolved,
      };
      editIssue(payload);
    },
  });

  useEffect(() => {
    if (orders) {
      const reducedOrders = orders.map((order: any) =>
        order?.orderNumber.toString()
      );
      setSelectedOrderNumber(reducedOrders);
      setSelectedOrders(orders);
    }
  }, [orders]);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      if (searchText) {
        dispatch(
          getCreateIssueSearchOrderRequest({
            searchTerm: searchText.trim(),
          })
        );
      } else {
        setOrderOptions([]);
      }
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchText]); // eslint-disable-line

  useEffect(() => {
    if (createIssueSearchResults) {
      const reducedOrders = createIssueSearchResults.map((order: any) =>
        order?.orderNumber.toString()
      );
      setOrderOptions(reducedOrders);
    }
  }, [createIssueSearchResults]);

  useEffect(() => {
    const orderIds = selectedOrders?.map((order) => order?.id);
    formik.setFieldValue("orderIds", orderIds);
  }, [selectedOrders]); // eslint-disable-line

  const editIssue = async (payload: any) => {
    setShowLoading(true);
    const response = await editIssueRequest(issueId, payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getIssueDetailsRequest({ issueId }));
      dispatch(
        getOrderIssuesRequest({
          page: 1,
          pageSize: 10,
          sortColumn: "is_resolved",
          sortOrder: "asc",
        })
      );
      onHide();
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <form onSubmit={formik.submitForm}>
      <Box sx={{ border: "1px solid #C4C4C4", borderRadius: 1, padding: 1 }}>
        <Typography variant="h5" color="black">
          Issue
        </Typography>
        <Box className="display-name-layout">
          <Typography className="label-txt">Issue Type</Typography>
          <FormControl fullWidth>
            {currentUser?.userType === userType.inspector ? (
              <Select
                labelId="issue-type-label"
                id="issue-type"
                name="subType"
                value={formik.values.subType}
                onChange={formik.handleChange}
              >
                <MenuItem value={"Acceptance"}>Acceptance Issue</MenuItem>
                <MenuItem value={"InspectorContact"}>Contact Issue</MenuItem>
              </Select>
            ) : (
              <Select
                labelId="issue-type-label"
                id="issue-type"
                name="subType"
                value={formik.values.subType}
                onChange={formik.handleChange}
              >
                <MenuItem value={"Update"}>Update Issue</MenuItem>
              </Select>
            )}
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Selected Orders</Typography>
          <FormControl
            fullWidth
            error={
              (formik.touched.orderIds || formik.submitCount > 0) &&
              Boolean(formik.errors.orderIds)
            }
          >
            <Autocomplete
              id="order-numbers"
              multiple
              options={orderOptions}
              value={selectedOrderNumber}
              onChange={(event, newValue) => {
                setSelectedOrderNumber(newValue);
                const allOrders = [...orders, ...createIssueSearchResults];
                const filterOrderNumbers = newValue.map(Number);
                const filtered = allOrders.filter((order) =>
                  filterOrderNumbers.includes(order.orderNumber)
                );
                setSelectedOrders(filtered);
                setOrderOptions([]);
              }}
              freeSolo
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    sx={{ height: 28, borderWidth: 1.5 }}
                    variant="outlined"
                    label={option}
                    color="primary"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              )}
            />
            <FormHelperText>
              {(formik.touched.orderIds || formik.submitCount > 0) &&
                formik.errors.orderIds}
            </FormHelperText>
          </FormControl>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Issue</Typography>
          <TextField
            name="description"
            fullWidth
            id="outlined-basic"
            label="Describe the Issue"
            variant="outlined"
            multiline
            onChange={formik.handleChange}
            value={formik.values.description}
            rows={2}
            error={
              (formik.touched.description || formik.submitCount > 0) &&
              Boolean(formik.errors.description)
            }
            helperText={
              (formik.touched.description || formik.submitCount > 0) &&
              (formik.errors.description as string)
            }
          />
        </Box>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onHide} type="reset">
            Cancel
          </Button>
          <Button
            sx={{ ml: 2 }}
            variant="contained"
            onClick={(e) => {
              formik.handleSubmit();
            }}
            disabled={!formik.isValid}
          >
            Save
          </Button>
        </Box>
      </Box>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showLoading && <Loading />}
    </form>
  );
}
